import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Button, Notice, Share2Icon, Text } from '@buffer-mono/popcorn'

import type { GuestMedia } from '~publish/helpers/guestMedia'

import { getGuestMediaAccess } from './guestMediaClient'

import styles from './PublicShareNowHandoff.module.css'

type DeviceShareState =
  | Readonly<{ status: 'idle' }>
  | Readonly<{ status: 'preparing' }>
  | Readonly<{ status: 'ready-files'; files: File[] }>
  | Readonly<{ status: 'ready-text' }>
  | Readonly<{ status: 'sharing' }>
  | Readonly<{ status: 'complete' }>
  | Readonly<{ status: 'error'; message: string }>

const IDLE_DEVICE_SHARE_STATE: DeviceShareState = { status: 'idle' }

function canShareFiles(files: File[]): boolean {
  try {
    return (
      typeof navigator.canShare === 'function' && navigator.canShare({ files })
    )
  } catch {
    return false
  }
}

export function PublicShareNowDeviceShare({
  text,
  media,
}: {
  text: string
  media: readonly GuestMedia[]
}): JSX.Element | null {
  const [deviceShareState, setDeviceShareState] = useState<DeviceShareState>(
    IDLE_DEVICE_SHARE_STATE,
  )
  const deviceShareControllerRef = useRef<AbortController>()

  useEffect(
    () => (): void => {
      deviceShareControllerRef.current?.abort()
    },
    [],
  )

  const prepareDeviceShare = useCallback(async (): Promise<void> => {
    deviceShareControllerRef.current?.abort()
    const controller = new AbortController()
    deviceShareControllerRef.current = controller
    setDeviceShareState({ status: 'preparing' })

    const fileSupportProbe = media.map(
      (asset) =>
        new File([], asset.fileName, {
          type: asset.mimeType,
          lastModified: asset.uploadedAt,
        }),
    )
    if (!canShareFiles(fileSupportProbe)) {
      deviceShareControllerRef.current = undefined
      setDeviceShareState(
        text.length > 0
          ? { status: 'ready-text' }
          : {
              status: 'error',
              message:
                'Your device cannot share these images. Choose a network to download them instead.',
            },
      )
      return
    }

    try {
      const files = await Promise.all(
        media.map(async (asset): Promise<File> => {
          const { accessUrl } = await getGuestMediaAccess(asset, {
            signal: controller.signal,
          })
          const response = await fetch(accessUrl, {
            credentials: 'omit',
            signal: controller.signal,
          })
          if (!response.ok) throw new Error('The image could not be loaded')

          const blob = await response.blob()
          return new File([blob], asset.fileName, {
            type: asset.mimeType,
            lastModified: asset.uploadedAt,
          })
        }),
      )

      if (controller.signal.aborted) return
      deviceShareControllerRef.current = undefined

      if (canShareFiles(files)) {
        setDeviceShareState({ status: 'ready-files', files })
      } else if (text.length > 0) {
        setDeviceShareState({ status: 'ready-text' })
      } else {
        setDeviceShareState({
          status: 'error',
          message:
            'Your device cannot share these images. Choose a network to download them instead.',
        })
      }
    } catch {
      if (controller.signal.aborted) return
      deviceShareControllerRef.current = undefined
      controller.abort()
      setDeviceShareState({
        status: 'error',
        message:
          'The images could not be prepared for device sharing. Choose a network to download them instead.',
      })
    }
  }, [media, text.length])

  const shareWithDevice = useCallback(async (): Promise<void> => {
    if (typeof navigator.share !== 'function') return

    if (
      media.length > 0 &&
      deviceShareState.status !== 'ready-files' &&
      deviceShareState.status !== 'ready-text'
    ) {
      await prepareDeviceShare()
      return
    }

    const shareData: ShareData =
      deviceShareState.status === 'ready-files'
        ? {
            files: deviceShareState.files,
            ...(text.length > 0 ? { text } : {}),
          }
        : { text }

    setDeviceShareState({ status: 'sharing' })
    try {
      await navigator.share(shareData)
      setDeviceShareState({ status: 'complete' })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        setDeviceShareState(IDLE_DEVICE_SHARE_STATE)
        return
      }
      setDeviceShareState({
        status: 'error',
        message: 'Your device could not open its share menu.',
      })
    }
  }, [deviceShareState, media.length, prepareDeviceShare, text])

  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function')
    return null

  const deviceShareButtonLabel =
    media.length > 0 &&
    deviceShareState.status !== 'ready-files' &&
    deviceShareState.status !== 'ready-text'
      ? 'Prepare device share'
      : deviceShareState.status === 'ready-text'
        ? 'Share text with your device'
        : 'Share with your device'

  return (
    <>
      <Button
        variant="secondary"
        className={styles.deviceShareButton}
        disabled={text.length === 0 && media.length === 0}
        loading={
          deviceShareState.status === 'preparing' ||
          deviceShareState.status === 'sharing'
        }
        onClick={shareWithDevice}
      >
        <Share2Icon />
        {deviceShareButtonLabel}
      </Button>
      {deviceShareState.status === 'ready-files' ? (
        <output aria-live="polite">
          <Text as="span" size="sm" color="subtle">
            {deviceShareState.files.length === 1
              ? 'Your image is ready to share.'
              : 'Your images are ready to share.'}
          </Text>
        </output>
      ) : null}
      {deviceShareState.status === 'ready-text' ? (
        <Notice variant="warning">
          <Notice.Text>
            Your device cannot share these image files. The device share menu
            will include your text only.
          </Notice.Text>
        </Notice>
      ) : null}
      {deviceShareState.status === 'complete' ? (
        <output aria-live="polite">
          <Text as="span" size="sm" color="subtle">
            Done sharing? Your draft will remain here.
          </Text>
        </output>
      ) : null}
      {deviceShareState.status === 'error' ? (
        <Notice variant="error">
          <Notice.Text>{deviceShareState.message}</Notice.Text>
        </Notice>
      ) : null}
    </>
  )
}
